import type { ImportContactRaw, ImportTarget } from "./api.js";

/**
 * Import limits (issue #33). Files above maxRows are refused with the row count, never truncated.
 * The web app commits in chunks of at most commitChunkRows rows and commitChunkBytes of JSON
 * (photos travel as base64), so a 25,000-row file never hits the API body limit.
 */
export const IMPORT_LIMITS = {
  maxRows: 25_000,
  commitChunkRows: 500,
  commitChunkBytes: 8 * 1024 * 1024,
} as const;

/**
 * Contact limits (issue #33). What matters for a phone is how many contacts one person receives;
 * the organization limit only protects against abuse. Operators can raise both per organization
 * in /ops (organizations.max_contacts_per_recipient, max_contacts).
 */
export const CONTACT_LIMITS = {
  /** Above this many contacts per person the app warns before saving. */
  perRecipientWarning: 15_000,
  /** Hard limit per person; revisit after the device test (stress test stage D). */
  perRecipient: 25_000,
  perOrganization: 50_000,
} as const;

/**
 * Rough duration of a first delivery. Kontaktfix writes one Graph request per contact, at most 4 at
 * a time per mailbox (Exchange allows about 10,000 requests per 10 minutes per mailbox) and 32 at a
 * time overall, so mailboxes run in parallel up to that cap.
 * shortcut: fixed rates from the Graph limits, refine with the stress test results (stage C).
 */
export function estimateInitialLoad(input: { contactsPerRecipient: number; writes: number }): {
  minMinutes: number;
  maxMinutes: number;
} {
  const perMailbox = { fast: 1000, slow: 600 }; // contacts per minute in one mailbox
  const overall = { fast: 5760, slow: 3840 }; // writes per minute across all mailboxes (32 parallel)
  const minutes = (speed: "fast" | "slow") =>
    Math.max(input.contactsPerRecipient / perMailbox[speed], input.writes / overall[speed]);
  return {
    minMinutes: Math.max(1, Math.round(minutes("fast"))),
    maxMinutes: Math.max(1, Math.ceil(minutes("slow"))),
  };
}

/** Lowercase, umlauts and the German sharp s folded, only letters and digits kept. */
export function normalizeHeader(header: string): string {
  return header
    .toLowerCase()
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue")
    .replace(/ß/g, "ss")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]/g, "");
}

/** Header names from Outlook exports (English and German) and common spreadsheets. */
const SYNONYMS: Record<ImportTarget, string[]> = {
  title: ["title", "anrede", "titel", "prefix", "salutation"],
  givenName: ["firstname", "givenname", "vorname", "first"],
  middleName: ["middlename", "weiterevornamen", "zweitervorname"],
  surname: ["lastname", "surname", "familyname", "nachname", "name", "last"],
  fullName: ["fullname", "displayname", "anzeigename", "vollstaendigername", "kontakt", "contact"],
  companyName: [
    "company",
    "companyname",
    "organization",
    "organisation",
    "firma",
    "unternehmen",
    "firmenname",
  ],
  department: ["department", "abteilung"],
  jobTitle: ["jobtitle", "position", "funktion", "berufsbezeichnung", "role"],
  // Outlook export names (English and German) for the fields of issue #30.
  nickName: ["nickname", "spitzname", "rufname"],
  profession: ["profession", "beruf"],
  officeLocation: ["officelocation", "office", "buero", "standort", "location", "site"],
  manager: [
    "managersname",
    "manager",
    "vorgesetzter",
    "vorgesetzte",
    "namedesvorgesetzten",
    "namedervorgesetzten",
  ],
  assistantName: [
    "assistantsname",
    "assistant",
    "assistantname",
    "assistent",
    "assistentin",
    "nameassistent",
    "namedesassistenten",
  ],
  imAddress: ["imaddress", "im", "imadresse", "chatadresse", "teams", "sip", "sipadresse"],
  email1: [
    "email",
    "emailaddress",
    "email1",
    "mail",
    "emailadresse",
    "e-mail",
    "email1address",
    "emailaddress1",
  ],
  email2: ["email2", "email2address", "email2adresse", "emailaddress2"],
  email3: ["email3", "email3address", "email3adresse", "emailaddress3"],
  businessPhone1: [
    "businessphone",
    "phone",
    "telefon",
    "telefongeschaeftlich",
    "worktelephone",
    "workphone",
    "tel",
    "telefonnummer",
    "phonenumber",
    "businessphone1",
  ],
  businessPhone2: ["businessphone2", "telefongeschaeftlich2", "workphone2"],
  mobilePhone: ["mobilephone", "mobile", "mobiltelefon", "handy", "natel", "cell", "cellphone", "mobil"],
  homePhone1: ["homephone", "telefonprivat", "privat", "homephone1"],
  homePhone2: ["homephone2", "telefonprivat2"],
  businessStreet: ["businessstreet", "strassegeschaeftlich", "street", "strasse", "adresse", "address"],
  businessPostalCode: [
    "businesspostalcode",
    "postleitzahlgeschaeftlich",
    "plz",
    "zip",
    "postalcode",
    "postleitzahl",
  ],
  businessCity: ["businesscity", "ortgeschaeftlich", "city", "ort", "stadt"],
  businessState: ["businessstate", "regiongeschaeftlich", "kanton", "state", "region"],
  businessCountry: ["businesscountryregion", "businesscountry", "landregiongeschaeftlich", "country", "land"],
  homeStreet: ["homestreet", "strasseprivat"],
  homePostalCode: ["homepostalcode", "postleitzahlprivat"],
  homeCity: ["homecity", "ortprivat"],
  homeCountry: ["homecountryregion", "homecountry", "landregionprivat"],
  website: ["webpage", "website", "webseite", "url", "homepage", "web"],
  notes: ["notes", "notizen", "bemerkung", "bemerkungen", "notiz", "kommentar", "comment"],
};

export function suggestMapping(columns: string[]): Record<string, ImportTarget | null> {
  const used = new Set<ImportTarget>();
  const result: Record<string, ImportTarget | null> = {};
  for (const column of columns) {
    const key = normalizeHeader(column);
    const match = (Object.entries(SYNONYMS) as [ImportTarget, string[]][]).find(
      ([target, names]) => !used.has(target) && names.map(normalizeHeader).includes(key),
    );
    result[column] = match ? match[0] : null;
    if (match) used.add(match[0]);
  }
  return result;
}

/** Turns spreadsheet rows into raw contacts using a column mapping. */
export function applyMapping(
  columns: string[],
  rows: string[][],
  mapping: Record<string, ImportTarget | null>,
): ImportContactRaw[] {
  return rows
    .map((row) => {
      const v: Partial<Record<ImportTarget, string>> = {};
      columns.forEach((column, i) => {
        const target = mapping[column];
        const value = row[i]?.trim();
        if (target && value) v[target] = v[target] ? `${v[target]} ${value}` : value;
      });
      let givenName = v.givenName;
      let surname = v.surname;
      if (v.fullName && !givenName && !surname) {
        const parts = v.fullName.split(/\s+/);
        surname = parts.length > 1 ? parts.pop() : parts[0];
        givenName = parts.length ? parts.join(" ") : undefined;
      }
      const address = (
        street?: string,
        postalCode?: string,
        city?: string,
        state?: string,
        country?: string,
      ) =>
        street || postalCode || city || country
          ? {
              street: street ?? "",
              postalCode: postalCode ?? "",
              city: city ?? "",
              state: state ?? "",
              country: country ?? "",
            }
          : null;
      const contact: ImportContactRaw = {
        title: v.title,
        givenName,
        middleName: v.middleName,
        surname,
        nickName: v.nickName,
        companyName: v.companyName,
        department: v.department,
        jobTitle: v.jobTitle,
        profession: v.profession,
        officeLocation: v.officeLocation,
        manager: v.manager,
        assistantName: v.assistantName,
        imAddress: v.imAddress,
        email1: v.email1,
        email2: v.email2,
        email3: v.email3,
        businessPhone1: v.businessPhone1,
        businessPhone2: v.businessPhone2,
        mobilePhone: v.mobilePhone,
        homePhone1: v.homePhone1,
        homePhone2: v.homePhone2,
        businessAddress: address(
          v.businessStreet,
          v.businessPostalCode,
          v.businessCity,
          v.businessState,
          v.businessCountry,
        ),
        homeAddress: address(v.homeStreet, v.homePostalCode, v.homeCity, undefined, v.homeCountry),
        website: v.website,
        notes: v.notes,
      };
      return contact;
    })
    .filter((c) => Object.values(c).some((x) => x !== undefined && x !== null && x !== ""));
}
